import type {
    AnswerChoiceRulesInput,
    FontWeightType,
    FormFieldFragment,
    FormFieldInput,
    FormPageFragment,
    FormPageFragment_fields as FormPageField,
    FormPageInput,
    FormUpdateInput,
    MultiPageFormFragment,
    MultiPageFormFragment_publishedVersion,
    MultiPageFormFragment_publishedVersion_pages_fields,
} from 'nutshell-graphql-types';
import {EntityType, FormFieldType} from 'nutshell-graphql-types';

import type {
    Field,
    FontStyle,
    FormFieldArrayRow,
    FormPage,
    HeaderFormField,
    ImageFormField,
    InputFormField,
    MultiPageForm,
    SubmitButtonField,
} from './types';

const INPUT_TYPES_FOR_STYLE = [
    FormFieldType.INPUT,
    FormFieldType.RADIO,
    FormFieldType.CHECKBOXES,
    FormFieldType.FILE,
    FormFieldType.URL,
];

const INPUT_TYPES_FOR_SAVE = [...INPUT_TYPES_FOR_STYLE, FormFieldType.DATE];
const DEFAULT_INPUT_STYLE = {
    color: '#4d4540',
    border: '.5px solid #d4d1cf',
    borderRadius: 3,
    fontSize: 13,
};

/** GraphQL `FontWeightType` allows only lowercase `normal` and `bold` (see Wf FontWeightType enum). */
function normalizeGraphqlFontWeight(weight: FontWeightType | string): FontWeightType {
    const lower = String(weight).toLowerCase();
    return (lower === 'bold' ? 'bold' : 'normal') as FontWeightType;
}

export function findFieldLocationByRowKey(
    pages: MultiPageForm['pages'] | undefined,
    rowKey: string
): {pageIndex: number; fieldIndex: number; field: FormFieldArrayRow} | null {
    if (!pages?.length) {
        return null;
    }
    for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
        const fields = pages[pageIndex]?.fields ?? [];
        for (let fieldIndex = 0; fieldIndex < fields.length; fieldIndex++) {
            const field = fields[fieldIndex];
            if (field?.key === rowKey) {
                return {pageIndex, fieldIndex, field};
            }
        }
    }
    return null;
}

export function fieldsFromPublishedVersionPages(
    publishedVersion: MultiPageFormFragment_publishedVersion | null | undefined
): MultiPageFormFragment_publishedVersion_pages_fields[] {
    if (!publishedVersion?.pages.length) {
        return [];
    }
    const out: MultiPageFormFragment_publishedVersion_pages_fields[] = [];
    for (const page of publishedVersion.pages) {
        out.push(...page.fields);
    }
    return out;
}

export function convertGraphQLResponseToMultiPageForm(form: MultiPageFormFragment): MultiPageForm {
    const editableVersion = form.editableVersion;
    const formStyles = editableVersion.styles === '' ? {} : JSON.parse(editableVersion.styles);

    return {
        id: form.id,
        backgroundColor: formStyles.backgroundColor,
        fontStyle: formStyles.fontStyle as FontStyle,
        inputStyle: getInputStyleFromPages(editableVersion.pages),
        pages: ensureEachPageHasSubmitButton(convertGraphQLPagesToFormPages(editableVersion.pages)),
    };
}

export function convertGraphQLPagesToFormPages(pages: FormPageFragment[]): FormPage[] {
    return pages.map((page) => ({
        id: page.id,
        fields: page.fields.map((field) => {
            const internal = convertGraphQLFieldToInternal(field);
            return {...internal, key: internal.id};
        }),
    }));
}

/** Client id for new submit rows until the server returns a real id. */
export function createDefaultSubmitButtonField(): FormFieldArrayRow {
    const id = `TEMP_SUBMIT_BUTTON_${Math.random().toString(36).slice(2, 11)}`;
    return {
        id,
        key: id,
        type: FormFieldType.SUBMIT_BUTTON,
        label: 'Submit',
        color: '#fff',
        backgroundColor: '#33c252',
        fontSize: 13,
        fontWeight: normalizeGraphqlFontWeight('normal'),
        justifyContent: 'flex-end',
        borderRadius: 3,
    };
}

function ensureEachPageHasSubmitButton(pages: FormPage[]): FormPage[] {
    return pages.map((page) => {
        if (page.fields.some((f) => f.type === FormFieldType.SUBMIT_BUTTON)) {
            return page;
        }
        return {...page, fields: [...page.fields, createDefaultSubmitButtonField()]};
    });
}

export function convertGraphQLFieldToInternal(
    field: FormFieldFragment | FormPageField
): Field | SubmitButtonField {
    if (field.type === FormFieldType.SUBMIT_BUTTON) {
        const fieldStyles = JSON.parse(field.styles);
        return {
            id: field.id,
            type: FormFieldType.SUBMIT_BUTTON,
            label: field.label,
            color: fieldStyles.color,
            backgroundColor: fieldStyles.backgroundColor,
            fontSize: fieldStyles.fontSize,
            fontWeight: normalizeGraphqlFontWeight(fieldStyles.fontWeight),
            justifyContent: fieldStyles.justifyContent,
            borderRadius: fieldStyles.borderRadius,
        };
    }

    const fieldStyles = JSON.parse(field.styles);
    const subfields = JSON.parse(field.subfields);

    return {
        id: field.id,
        type: field.type,
        fieldName: field.fieldName,
        label: field.label,
        entityType: field.entityName ?? EntityType.CONTACT,
        entityTypes: field.entityTypes,
        placeholder: field.placeholder ?? '',
        border: fieldStyles.border,
        borderRadius: fieldStyles.borderRadius,
        fontSize: fieldStyles.fontSize,
        fontWeight: fieldStyles.fontWeight,
        textAlign: fieldStyles.textAlign,
        justifyContent: fieldStyles.justifyContent,
        color: fieldStyles.color,
        inputColor: fieldStyles.inputColor ?? fieldStyles.color,
        backgroundColor: fieldStyles.backgroundColor,
        attributeId: field.attribute ? field.attribute.id : null,
        isRequired: field.isRequired,
        isRemovable: field.isRemovable,
        width: fieldStyles.width,
        imageUrl: field.image ? field.image.imageUrl : null,
        subfields,
        isSeparateFields: field.isSeparateFields,
        rules: field.rules,
        defaultValues: field.defaultValues,
    } as Field;
}

function getInputStyleFromPages(pages: FormPageFragment[]) {
    for (const page of pages) {
        const inputField = page.fields.find((f) => INPUT_TYPES_FOR_STYLE.some((t) => t === f.type));
        if (inputField) {
            return JSON.parse(inputField.styles);
        }
    }

    return DEFAULT_INPUT_STYLE;
}

export function convertMultiPageFormToGraphQL(form: MultiPageForm): FormUpdateInput {
    return {
        form: {
            id: form.id,
            styles: {
                backgroundColor: form.backgroundColor,
                fontStyle: form.fontStyle
                    ? {
                          name: form.fontStyle.name,
                          fontFamily: form.fontStyle.fontFamily,
                          inheritWhenEmbedded: Boolean(form.fontStyle.inheritWhenEmbedded),
                      }
                    : {
                          name: 'Arial',
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          inheritWhenEmbedded: true,
                      },
            },
            pages: convertFormPagesToGraphQLInput(form),
        },
    };
}

function convertFormPagesToGraphQLInput(form: MultiPageForm): FormPageInput[] {
    return form.pages.map((page) => ({
        id: page.id,
        fields: page.fields.map((field) =>
            convertFieldToGraphQLInput(field as Field | SubmitButtonField, form.inputStyle)
        ),
    }));
}

function convertFieldToGraphQLInput(
    field: Field | SubmitButtonField,
    inputStyle: MultiPageForm['inputStyle']
): FormFieldInput {
    if (INPUT_TYPES_FOR_SAVE.some((t) => t === field.type)) {
        const f = field as InputFormField;

        return {
            id: f.id,
            type: f.type,
            label: f.label,
            fieldName: f.fieldName,
            attributeId: f.attributeId,
            entityName: f.entityType,
            placeholder: f.placeholder,
            isRequired: f.isRequired,
            styles: inputStyle,
            isSeparateFields: f.isSeparateFields,
            subfields: f.subfields,
            rules: (f.rules ?? []).map(
                (rule): AnswerChoiceRulesInput => ({
                    answerChoiceId: rule.answerChoice.id,
                    assignmentObjectsApiIds: rule.objectsToAssign.map((obj) => obj.id),
                })
            ),
            defaultValues: f.defaultValues,
        };
    }

    if (field.type === FormFieldType.SUBMIT_BUTTON) {
        const f = field;

        return {
            id: f.id.startsWith('TEMP_SUBMIT_BUTTON') ? null : f.id,
            type: f.type,
            label: f.label,
            styles: {
                backgroundColor: f.backgroundColor,
                fontSize: f.fontSize,
                fontWeight: normalizeGraphqlFontWeight(f.fontWeight),
                justifyContent: f.justifyContent,
                borderRadius: f.borderRadius,
                color: f.color,
            },
        };
    }

    if (field.type === FormFieldType.IMAGE) {
        const f = field as ImageFormField;

        return {
            id: f.id,
            type: f.type,
            label: f.label,
            imageUrl: f.imageUrl,
            styles: {
                width: f.width,
                justifyContent: f.justifyContent,
            },
        };
    }

    const f = field as HeaderFormField;

    return {
        id: f.id,
        type: f.type,
        label: f.label,
        fieldName: null,
        entityName: null,
        styles: {
            color: f.color,
            fontSize: f.fontSize,
            fontWeight: f.fontWeight,
            textAlign: f.textAlign,
        },
    };
}
